import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import IconUpload from '@mui/icons-material/AddPhotoAlternateOutlined';
import IconCrop from '@mui/icons-material/CropOutlined';
import IconBack from '@mui/icons-material/ArrowBackOutlined';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import AttachmentThumb from '../../components/patient-attachments/AttachmentThumb';
import PatientAttachmentUploadPopup from '../PatientAttachmentUploadPopup/PatientAttachmentUploadPopup';
import { DataProviderInstance } from '../../api/DataProvider';
import { FULL_CROP, applicationViewLabel, normalizeCrop } from '../../pages/appointment/components/applicationMap/applicationFigures';
import {
  APPLICATION_PHOTO_CONTEXT,
  applicationPhotoLabel,
  buildApplicationPhotoFields,
  filterApplicationPhotos,
  formatApplicationPhotoDate,
  sortPhotosForView,
} from '../../pages/appointment/components/applicationMap/applicationPhotos';
import PhotoCropper from './PhotoCropper';
import styles from './ApplicationPhotoPopup.module.css';

const THUMB_SIZE = 96;

/**
 * Foto do paciente como fundo de uma vista do mapa de aplicação. Dois passos:
 * escolher uma foto já enviada para o mapa (ou enviar uma nova) e enquadrar o
 * recorte. "Usar foto" devolve `onSelect({ attachmentId, crop })`; o arquivo
 * nunca é alterado.
 *
 * As fotos têm contexto próprio (`application_map`): não se misturam com as
 * Fotos de evolução nem com os Anexos do paciente.
 *
 * `background`: fundo atual da vista ({ attachmentId, crop } ou null), para
 * reabrir o enquadramento da foto em uso.
 */
const ApplicationPhotoPopup = ({ patientId, appointmentId, view, currentDate, background, onSelect, OnHide }) => {
  const { ListPatientAttachments } = DataProviderInstance();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [crop, setCrop] = useState(FULL_CROP);
  const [showUpload, setShowUpload] = useState(false);

  const sorted = useMemo(() => sortPhotosForView(photos, view), [photos, view]);

  function loadPhotos(callback) {
    ListPatientAttachments(patientId, APPLICATION_PHOTO_CONTEXT, (response) => {
      const list = response && response.status === 'SUCCESS' ? filterApplicationPhotos(response.data || []) : [];
      setPhotos(list);
      setLoading(false);
      if (callback) callback(list);
    });
  }

  useEffect(() => {
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  function choose(photo) {
    setSelected(photo);
    const inUse = background && String(background.attachmentId) === String(photo.id);
    setCrop(inUse ? normalizeCrop(background.crop) : FULL_CROP);
  }

  function uploadHideHandler(success) {
    setShowUpload(false);
    if (!success) return;
    setLoading(true);
    loadPhotos((list) => {
      // A foto recém-enviada é a de maior id: já vai para o enquadramento
      const newest = list.reduce((best, photo) => (!best || Number(photo.id) > Number(best.id) ? photo : best), null);
      if (newest) choose(newest);
    });
  }

  function confirm() {
    onSelect({ attachmentId: selected.id, crop: normalizeCrop(crop) });
    OnHide();
  }

  function renderPicker() {
    if (loading) {
      return (
        <div className={styles.message}>
          <CircularProgress size={20} />
          <span>Carregando fotos...</span>
        </div>
      );
    }
    if (sorted.length === 0) {
      return (
        <div className={styles.message}>
          <span>Este paciente ainda não tem fotos para o mapa de aplicação. Envie uma foto para usar como fundo.</span>
        </div>
      );
    }
    return (
      <div className={styles.grid}>
        {sorted.map((photo) => {
          const inUse = background && String(background.attachmentId) === String(photo.id);
          return (
            <button
              key={photo.id}
              type="button"
              className={`${styles.tile} ${inUse ? styles.tileInUse : ''}`}
              onClick={() => choose(photo)}
              title={photo.note || ''}
            >
              <AttachmentThumb attachment={photo} size={THUMB_SIZE} chipLabel={inUse ? 'Em uso' : undefined} />
              <span className={styles.tileTitle}>{applicationPhotoLabel(photo)}</span>
              <span className={styles.tileMeta}>{formatApplicationPhotoDate(photo)}</span>
            </button>
          );
        })}
      </div>
    );
  }

  function renderContent() {
    if (selected) {
      return (
        <div className={styles.container}>
          <span className={styles.hint}>
            Arraste o recorte para posicionar e os cantos para ajustar o tamanho. O recorte vira a figura da vista {applicationViewLabel(view)}; a foto original não muda.
          </span>
          <PhotoCropper attachmentId={selected.id} crop={crop} onChange={setCrop} />
        </div>
      );
    }
    return (
      <div className={styles.container}>
        <div className={styles.pickerHead}>
          <span className={styles.hint}>Escolha a foto que será o fundo da vista {applicationViewLabel(view)}. As fotos desta vista aparecem primeiro.</span>
          <CustomButton
            variant="outlined"
            style="primary"
            label="Enviar nova foto"
            icon={<IconUpload />}
            onClick={() => setShowUpload(true)}
          />
        </div>
        {renderPicker()}
      </div>
    );
  }

  function renderFooter() {
    return (
      <div className={styles.footer}>
        <div>
          {selected && (
            <CustomButton variant="outlined" style="secondary" label="Escolher outra" icon={<IconBack />} onClick={() => setSelected(null)} />
          )}
        </div>
        <div className={styles.footerRight}>
          {selected && (
            <CustomButton variant="outlined" style="secondary" label="Foto inteira" onClick={() => setCrop(FULL_CROP)} />
          )}
          <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={OnHide} />
          {selected && (
            <CustomButton variant="contained" style="primary" label="Usar foto" icon={<IconCrop />} onClick={confirm} />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupBase
        Title={selected ? 'Enquadrar a foto' : 'Foto do paciente como fundo'}
        Content={renderContent()}
        Footer={renderFooter()}
        OnClose={OnHide}
        maxWidth="md"
      />

      {showUpload && (
        <PatientAttachmentUploadPopup
          title={`Enviar foto para a vista ${applicationViewLabel(view)}`}
          patientId={patientId}
          appointmentId={appointmentId}
          context={APPLICATION_PHOTO_CONTEXT}
          defaultDate={currentDate}
          capture={true}
          accept="image/*"
          maxFiles={1}
          buildItemPayload={() => buildApplicationPhotoFields(view)}
          OnHide={uploadHideHandler}
        />
      )}
    </>
  );
};

export default ApplicationPhotoPopup;
